/**
 * WP15 — HUD overlay: licznik salda zł + telefonik.
 * WAVE5-W1 — klikalna moneta dziennej nagrody „Odbierz 50 zł".
 * Saldo live: subskrybuje balanceBus (nagrody z mini-gier przez RunClient,
 * zakupy przez OutfitStore) i dosyła autorytatywne saldo z /api/me.
 * UI PL z diakrytykami, cele dotykowe ≥ MIN_TOUCH, zero-reading (ikony).
 */
import type Phaser from 'phaser';
import { BaseScene } from './BaseScene.js';
import { MIN_TOUCH } from './UiKit.js';
import { getSceneRouter } from './SceneRouter.js';
import { balanceBus, formatZl } from './balanceBus.js';
import { OutfitStore } from '../avatar/outfitStore.js';
import { fetchMe } from '../avatar/api.js';
import { claimDailyReward, fetchDailyStatus } from './dailyRewardClient.js';
import { audioBus } from './AudioBus.js';

export class HudScene extends BaseScene {
  static readonly KEY = 'Hud';

  private balanceText: Phaser.GameObjects.Text | null = null;
  private unsub: (() => void) | null = null;
  private dailyCoin: Phaser.GameObjects.Container | null = null;
  private dailyClaiming = false;

  constructor() {
    super(HudScene.KEY);
  }

  create(): void {
    this.cameras.main.setBackgroundColor('rgba(0,0,0,0)');

    this.buildBalanceChip();
    this.buildPhoneButton();

    // seed z cache offline (natychmiast) + subskrypcja live
    const cached = OutfitStore.load().balanceZl;
    if (Number.isFinite(cached)) balanceBus.set(cached);
    this.unsub = balanceBus.subscribe((b) => this.renderBalance(b));

    // autorytatywne saldo z serwera (jeśli online)
    void this.refreshFromServer();
    void this.refreshDailyReward();

    this.events.once('shutdown', () => this.teardown());
    this.events.once('destroy', () => this.teardown());
  }

  private buildBalanceChip(): void {
    const x = 24;
    const y = 24;
    const w = 168;
    const h = MIN_TOUCH;
    const chip = this.add
      .rectangle(x, y, w, h, 0xfff8e1, 0.96)
      .setOrigin(0, 0)
      .setStrokeStyle(3, 0xffd54f)
      .setDepth(5000)
      .setScrollFactor(0);
    chip.setName('hud-balance-chip');

    this.add
      .text(x + 24, y + h / 2, '🪙', { fontSize: '30px' })
      .setOrigin(0.5)
      .setDepth(5001)
      .setScrollFactor(0);

    this.balanceText = this.add
      .text(x + 48, y + h / 2, formatZl(balanceBus.get()), {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '26px',
        color: '#5d4037',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(5001)
      .setScrollFactor(0);
    this.balanceText.setName('hud-balance-value');
    // dane dla E2E / audytu
    this.balanceText.setData('balanceZl', balanceBus.get() ?? -1);
  }

  private buildPhoneButton(): void {
    const size = Math.max(MIN_TOUCH, 64);
    const x = this.gameW - size / 2 - 20;
    const y = 24 + size / 2;
    const bg = this.add
      .circle(x, y, size / 2, 0xffffff, 0.95)
      .setStrokeStyle(3, 0xffb6c1)
      .setInteractive({ useHandCursor: true })
      .setDepth(5000)
      .setScrollFactor(0);
    this.add
      .text(x, y, '📱', { fontSize: '34px' })
      .setOrigin(0.5)
      .setDepth(5001)
      .setScrollFactor(0);
    bg.setName('hud-phone-button');
    bg.on('pointerdown', (p: Phaser.Input.Pointer) => {
      p.event?.stopPropagation?.();
      getSceneRouter().launchOverlay('PhoneOverlay');
    });
  }

  /** Klikalna moneta dziennej nagrody — pod chipem salda. */
  private showDailyCoin(amountZl: number): void {
    if (this.dailyCoin) return;

    const x = 24 + 84;
    const y = 24 + MIN_TOUCH + 12 + 36;
    const size = Math.max(MIN_TOUCH, 72);

    const container = this.add.container(x, y).setDepth(5002).setScrollFactor(0);
    container.setName('hud-daily-coin');

    const bg = this.add
      .circle(0, 0, size / 2, 0xffe082, 0.98)
      .setStrokeStyle(4, 0xff8f00)
      .setInteractive({ useHandCursor: true });
    bg.setName('hud-daily-coin-hit');

    const emoji = this.add.text(0, -10, '🪙', { fontSize: '36px' }).setOrigin(0.5);
    const label = this.add
      .text(0, 22, `Odbierz ${amountZl} zł`, {
        fontFamily: 'system-ui, sans-serif',
        fontSize: '14px',
        color: '#5d4037',
        fontStyle: 'bold',
        align: 'center',
      })
      .setOrigin(0.5);

    container.add([bg, emoji, label]);

    // delikatny puls zachęty
    this.tweens.add({
      targets: container,
      scale: 1.08,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    bg.on('pointerdown', (p: Phaser.Input.Pointer) => {
      p.event?.stopPropagation?.();
      void this.onDailyClaim();
    });

    this.dailyCoin = container;
  }

  private hideDailyCoin(animate = false): void {
    const coin = this.dailyCoin;
    if (!coin) return;
    this.dailyCoin = null;
    this.tweens.killTweensOf(coin);
    if (!animate) {
      coin.destroy(true);
      return;
    }
    this.tweens.add({
      targets: coin,
      scale: 1.6,
      alpha: 0,
      y: coin.y - 40,
      duration: 450,
      ease: 'Back.easeIn',
      onComplete: () => coin.destroy(true),
    });
  }

  private async onDailyClaim(): Promise<void> {
    if (this.dailyClaiming || !this.dailyCoin) return;
    this.dailyClaiming = true;
    try {
      audioBus.playSfx('sfx_click');
      const res = await claimDailyReward();
      if (!this.sys.isActive()) return;
      if (!res.ok) {
        this.dailyClaiming = false;
        return;
      }
      if (res.data.claimed && typeof res.data.balanceZl === 'number') {
        balanceBus.set(res.data.balanceZl);
        OutfitStore.saveBalance(res.data.balanceZl);
        audioBus.playSfx('sfx_reward');
        this.hideDailyCoin(true);
      } else {
        // już odebrane — schowaj bez fanfary
        balanceBus.set(res.data.balanceZl);
        OutfitStore.saveBalance(res.data.balanceZl);
        this.hideDailyCoin(false);
      }
    } finally {
      this.dailyClaiming = false;
    }
  }

  private async refreshDailyReward(): Promise<void> {
    const res = await fetchDailyStatus();
    if (!this.sys.isActive()) return;
    if (!res.ok) return;
    if (res.data.eligible) {
      this.showDailyCoin(res.data.amountZl);
    } else {
      this.hideDailyCoin(false);
    }
  }

  private renderBalance(balance: number | null): void {
    if (!this.balanceText) return;
    this.balanceText.setText(formatZl(balance));
    this.balanceText.setData('balanceZl', balance ?? -1);
  }

  private async refreshFromServer(): Promise<void> {
    const res = await fetchMe();
    if (!this.sys.isActive()) return;
    if (res.ok) balanceBus.set(res.data.balanceZl);
  }

  private teardown(): void {
    this.unsub?.();
    this.unsub = null;
    this.balanceText = null;
    if (this.dailyCoin) {
      this.tweens.killTweensOf(this.dailyCoin);
      this.dailyCoin.destroy(true);
      this.dailyCoin = null;
    }
    this.dailyClaiming = false;
  }
}

/** Uruchom HUD jako overlay, jeśli jeszcze nie działa (wołane przy wejściu w świat). */
export function ensureHudOverlay(game: Phaser.Game): void {
  const mgr = game.scene;
  if (mgr.isActive(HudScene.KEY) || mgr.isPaused(HudScene.KEY)) return;
  (mgr as unknown as { run: (key: string) => void }).run(HudScene.KEY);
}

export default HudScene;
